import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@fleetcore/shared'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { env } from '../env'
import { DEFAULT_NAMESPACE, NAMESPACES, resources } from './resources'

export const LOCALE_STORAGE_KEY = 'fleetcore.locale'

function readStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    return stored && isSupportedLocale(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Order matters: an explicit choice the user made in this browser wins over the
 * browser's own language, which in turn wins over the deployment default. The
 * signed-in user's stored preference is applied later by the auth layer calling
 * `setLocale`, because it is only known once the session exists.
 */
export function detectInitialLocale(): SupportedLocale {
  const stored = readStoredLocale()
  if (stored) return stored

  const navigatorLanguages = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [])
  for (const candidate of navigatorLanguages) {
    const primary = candidate.split('-')[0]?.toLowerCase() ?? ''
    if (isSupportedLocale(primary)) return primary
  }

  return env.defaultLocale ?? DEFAULT_LOCALE
}

/** Languages written right to left. The whole layout mirrors for them. */
export const RTL_LOCALES: readonly SupportedLocale[] = ['ar']

export function directionOf(locale: SupportedLocale): 'rtl' | 'ltr' {
  return RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'
}

/**
 * Keeps `<html lang>` truthful — screen readers pick pronunciation from it — and
 * `<html dir>`, which mirrors the layout for Arabic.
 */
export function applyDocumentLocale(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = locale
  document.documentElement.dir = directionOf(locale)
}

export function setLocale(locale: SupportedLocale, persist = true): Promise<unknown> {
  if (persist) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
      /* Storage denied — the choice simply does not survive the tab. */
    }
  }
  applyDocumentLocale(locale)
  return i18n.changeLanguage(locale)
}

/**
 * The language for text the SERVER writes — report files, statement wording — which
 * exists in French and English only. Arabic readers get French there, the language a
 * Moroccan document is usually in, rather than English.
 */
export function serverLocale(locale: SupportedLocale): 'fr' | 'en' {
  return locale === 'en' ? 'en' : 'fr'
}

/**
 * Applies a signed-in user's profile language, unless this browser holds an explicit
 * choice from the language menu: that choice wins. Without this, picking Arabic (which
 * a profile cannot hold) was undone by the profile on every reload and every login.
 */
export function applyUserLocale(profileLocale: SupportedLocale | null | undefined): void {
  const chosen = readStoredLocale()
  if (chosen !== null) {
    void setLocale(chosen, false)
    return
  }
  if (profileLocale) void setLocale(profileLocale, false)
}

/** "from → to", pointing the way the language reads: ← in Arabic. */
export function forwardArrow(): string {
  return directionOf(currentLocale()) === 'rtl' ? '←' : '→'
}

export function currentLocale(): SupportedLocale {
  return isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

export function initI18n(initialLocale: SupportedLocale = detectInitialLocale()): typeof i18n {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources,
      lng: initialLocale,
      // French, not English. A missing English string is a bug we want to see in
      // review; a missing French string must never fall through to English for a
      // French customer, so the fallback chain has exactly one link.
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NAMESPACE,
      returnNull: false,
      interpolation: {
        // React escapes for us; double-escaping mangles apostrophes, of which
        // French copy has a great many.
        escapeValue: false,
      },
      react: { useSuspense: false },
    })
  }

  applyDocumentLocale(initialLocale)
  return i18n
}

export { i18n }
